import React, { useEffect, useState } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { motion } from 'framer-motion';
import { Smartphone, Mail, Clock, Bell, MapPin, Zap, Check, ChevronRight, CreditCard, ShoppingBag, Users, BarChart, Layers, SmartphoneNfc } from 'lucide-react';
const API_URL = 'https://cru-forms.cruio.workers.dev/api/digital-wallet';
export function DigitalWallet() {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  // Form state
  const [formData, setFormData] = useState({
    businessName: '',
    website: '',
    loyaltyApp: '',
    hasRetail: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          businessName: formData.businessName,
          email: formData.email,
          website: formData.website,
          loyaltyApp: formData.loyaltyApp,
          hasRetail: formData.hasRetail,
          message: formData.message
        })
      });
      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          businessName: '',
          website: '',
          loyaltyApp: '',
          hasRetail: '',
          email: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };
  const fadeIn = {
    initial: {
      opacity: 0,
      y: 20
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true
    },
    transition: {
      duration: 0.5
    }
  };
  return <div className="min-h-screen bg-[#FAF7F2] font-['Inter']" data-id="element-392">
      <Header data-id="element-393" />

      {/* 1. HERO SECTION */}
      <section className="pt-32 pb-20 px-6 md:px-10 bg-[#FAF7F2]" data-id="element-394">
        <div className="max-w-[1400px] mx-auto" data-id="element-395">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-396">
            <div className="flex flex-col text-left" data-id="element-397">
              <motion.h1 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="text-[#2C2416] text-[clamp(48px,6vw,80px)] font-light leading-[1.1] tracking-[-3px] mb-6" data-id="element-398">
                Digital Wallet Passes for Shopify{' '}
                <span className="text-[#D4A574]" data-id="element-399">Retention</span>
              </motion.h1>

              <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} className="text-[#8B7355] text-xl md:text-2xl mb-10 max-w-xl leading-relaxed" data-id="element-400">
                Turn your loyalty program into a tap-and-scan experience. Apple
                Wallet and Google Wallet passes that work with your existing
                Shopify loyalty apps.
              </motion.p>

              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.3
            }} className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto" data-id="element-401">
                <a href="#form" className="bg-[#2C2416] text-[#FAF7F2] px-9 py-[18px] rounded-full font-medium tracking-[0.5px] hover:scale-105 transition-all duration-300 hover:bg-[#D4A574] text-center" data-id="element-402">
                  Get Started
                </a>
                <a href="#how-it-works" className="bg-transparent border-2 border-[#2C2416] text-[#2C2416] px-9 py-[18px] rounded-full font-medium tracking-[0.5px] hover:scale-105 transition-all duration-300 hover:bg-[#2C2416] hover:text-[#FAF7F2] text-center" data-id="element-403">
                  See How It Works
                </a>
              </motion.div>
            </div>

            {/* Phone Mockup Placeholder */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="flex justify-center items-center" data-id="element-404">
              <div className="relative w-[300px] h-[600px] bg-[#2C2416] rounded-[40px] p-4 shadow-2xl border-8 border-[#2C2416]" data-id="element-405">
                <div className="w-full h-full bg-white rounded-[32px] overflow-hidden relative flex flex-col" data-id="element-406">
                  {/* Status Bar */}
                  <div className="h-8 w-full bg-gray-100 flex justify-between px-6 items-center text-[10px] font-bold text-gray-800" data-id="element-407">
                    <span data-id="element-408">9:41</span>
                    <div className="flex gap-1" data-id="element-409">
                      <div className="w-4 h-2.5 bg-gray-800 rounded-[1px]" data-id="element-410"></div>
                      <div className="w-0.5 h-2.5 bg-gray-800 rounded-[1px]" data-id="element-411"></div>
                    </div>
                  </div>

                  {/* Wallet Pass */}
                  <div className="flex-1 bg-gray-100 p-4 flex flex-col items-center pt-10" data-id="element-412">
                    <div className="w-full bg-[#2C2416] rounded-xl overflow-hidden shadow-lg relative" data-id="element-413">
                      {/* Pass Header */}
                      <div className="bg-[#2C2416] p-4 flex justify-between items-center" data-id="element-414">
                        <div className="w-8 h-8 bg-[#D4A574] rounded-full flex items-center justify-center text-[#2C2416] font-bold text-xs" data-id="element-415">
                          MC
                        </div>
                        <span className="text-[#D4A574] text-xs font-bold uppercase tracking-wider" data-id="element-416">
                          VIP MEMBER
                        </span>
                      </div>

                      {/* Pass Body */}
                      <div className="bg-[#FAF7F2] p-5" data-id="element-417">
                        <div className="flex justify-between items-end mb-6" data-id="element-418">
                          <div data-id="element-419">
                            <span className="text-[#8B7355] text-xs uppercase tracking-wider block mb-1" data-id="element-420">
                              Points Balance
                            </span>
                            <span className="text-[#2C2416] text-4xl font-light" data-id="element-421">
                              2,450
                            </span>
                          </div>
                          <div className="text-right" data-id="element-422">
                            <span className="text-[#8B7355] text-xs uppercase tracking-wider block mb-1" data-id="element-423">
                              Tier
                            </span>
                            <span className="text-[#2C2416] text-lg font-medium" data-id="element-424">
                              Gold
                            </span>
                          </div>
                        </div>

                        {/* Barcode */}
                        <div className="bg-white p-3 rounded-lg flex justify-center items-center mb-2" data-id="element-425">
                          <div className="h-12 w-full bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InRyYW5zcGFyZW50Ii8+PHJlY3QgeD0iMTAiIHk9IjEwIiB3aWR0aD0iMiIgaGVpZ2h0PSI4MCIgZmlsbD0iIzAwMCIvPjxyZWN0IHg9IjE1IiB5PSIxMCIgd2lkdGg9IjQiIGhlaWdodD0iODAiIGZpbGw9IiMwMDAiLz48cmVjdCB4PSIyMiIgeT0iMTAiIHdpZHRoPSIxIiBoZWlnaHQ9IjgwIiBmaWxsPSIjMDAwIi8+PHJlY3QgeD0iMjYiIHk9IjEwIiB3aWR0aD0iMyIgaGVpZ2h0PSI4MCIgZmlsbD0iIzAwMCIvPjxyZWN0IHg9IjMyIiB5PSIxMCIgd2lkdGg9IjIiIGhlaWdodD0iODAiIGZpbGw9IiMwMDAiLz48cmVjdCB4PSIzNiIgeT0iMTAiIHdpZHRoPSI1IiBoZWlnaHQ9IjgwIiBmaWxsPSIjMDAwIi8+PHJlY3QgeD0iNDQiIHk9IjEwIiB3aWR0aD0iMiIgaGVpZ2h0PSI4MCIgZmlsbD0iIzAwMCIvPjxyZWN0IHg9IjQ4IiB5PSIxMCIgd2lkdGg9IjEiIGhlaWdodD0iODAiIGZpbGw9IiMwMDAiLz48cmVjdCB4PSI1MiIgeT0iMTAiIHdpZHRoPSIzIiBoZWlnaHQ9IjgwIiBmaWxsPSIjMDAwIi8+PHJlY3QgeD0iNTgiIHk9IjEwIiB3aWR0aD0iMiIgaGVpZ2h0PSI4MCIgZmlsbD0iIzAwMCIvPjxyZWN0IHg9IjYyIiB5PSIxMCIgd2lkdGg9IjQiIGhlaWdodD0iODAiIGZpbGw9IiMwMDAiLz48cmVjdCB4PSI2OCIgeT0iMTAiIHdpZHRoPSIxIiBoZWlnaHQ9IjgwIiBmaWxsPSIjMDAwIi8+PHJlY3QgeD0iNzIiIHk9IjEwIiB3aWR0aD0iMyIgaGVpZ2h0PSI4MCIgZmlsbD0iIzAwMCIvPjxyZWN0IHg9Ijc4IiB5PSIxMCIgd2lkdGg9IjIiIGhlaWdodD0iODAiIGZpbGw9IiMwMDAiLz48L3N2Zz4=')] bg-repeat-x opacity-80" data-id="element-426"></div>
                        </div>
                        <div className="text-center text-[10px] text-[#8B7355] tracking-widest" data-id="element-427">
                          1234 5678 9012
                        </div>
                      </div>
                    </div>

                    {/* Notification */}
                    <motion.div initial={{
                    y: 50,
                    opacity: 0
                  }} animate={{
                    y: 0,
                    opacity: 1
                  }} transition={{
                    delay: 1.5,
                    duration: 0.5
                  }} className="mt-6 w-full bg-white/90 backdrop-blur-sm p-3 rounded-xl shadow-lg border border-gray-200" data-id="element-428">
                      <div className="flex gap-3" data-id="element-429">
                        <div className="w-10 h-10 bg-[#2C2416] rounded-lg flex items-center justify-center text-[#D4A574]" data-id="element-430">
                          <Bell size={20} data-id="element-431" />
                        </div>
                        <div data-id="element-432">
                          <div className="text-xs font-bold text-gray-900" data-id="element-433">
                            Modern Commerce
                          </div>
                          <div className="text-xs text-gray-600 leading-tight" data-id="element-434">
                            You're near our store! Pop in to use your $10
                            reward.
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 2. PROBLEM SECTION */}
      <section className="py-24 px-6 md:px-10 bg-white" data-id="element-435">
        <div className="max-w-[1400px] mx-auto" data-id="element-436">
          <div className="text-center mb-16" data-id="element-437">
            <h2 className="text-[#2C2416] text-[clamp(36px,5vw,48px)] font-light tracking-[-2px] mb-4" data-id="element-438">
              Your Loyalty Program Lives in an App They'll Never Open
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-439">
            {[{
            icon: <Smartphone className="w-10 h-10 text-[#D4A574]" data-id="element-440" />,
            title: 'App Fatigue',
            desc: "Customers won't download another app for your loyalty program."
          }, {
            icon: <Mail className="w-10 h-10 text-[#2C2416]" data-id="element-441" />,
            title: 'Email Blindness',
            desc: 'Points reminders get lost in crowded inboxes.'
          }, {
            icon: <Clock className="w-10 h-10 text-[#D4A574]" data-id="element-442" />,
            title: 'Checkout Friction',
            desc: 'Asking for email slows down the POS experience.'
          }].map((item, index) => <motion.div key={index} {...fadeIn} transition={{
            delay: index * 0.1
          }} className="bg-white p-10 rounded-xl border border-[#D4A57420] shadow-lg text-center hover:-translate-y-1 transition-all duration-300" data-id="element-443">
                <div className="w-20 h-20 bg-[#FAF7F2] rounded-2xl flex items-center justify-center mb-6 mx-auto" data-id="element-444">
                  {item.icon}
                </div>
                <h3 className="text-xl font-medium text-[#2C2416] mb-4" data-id="element-445">
                  {item.title}
                </h3>
                <p className="text-[#8B7355] leading-relaxed" data-id="element-446">{item.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* 3. HOW IT WORKS SECTION */}
      <section id="how-it-works" className="py-24 px-6 md:px-10 bg-[#FAF7F2]" data-id="element-447">
        <div className="max-w-[1400px] mx-auto" data-id="element-448">
          <div className="text-center mb-16" data-id="element-449">
            <h2 className="text-[#2C2416] text-[clamp(36px,5vw,48px)] font-light tracking-[-2px] mb-4" data-id="element-450">
              Loyalty That Lives in Their Wallet
            </h2>
          </div>

          {/* Steps */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 relative mb-20" data-id="element-451">
            {/* Connecting Line (Desktop) */}
            <div className="hidden md:block absolute top-10 left-0 w-full h-0.5 bg-[#D4A57420] -z-10" data-id="element-452"></div>

            {[{
            step: '01',
            text: 'Customer joins your loyalty program via Yotpo or Smile.io etc.'
          }, {
            step: '02',
            text: 'Pass delivered via email or SMS through Klaviyo.'
          }, {
            step: '03',
            text: 'Customer saves to Apple or Google Wallet with one tap.'
          }, {
            step: '04',
            text: 'Scan at checkout and customer is identified and points applied.'
          }].map((item, index) => <motion.div key={index} {...fadeIn} transition={{
            delay: index * 0.1
          }} className="flex flex-col items-center text-center" data-id="element-453">
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mb-6 border-4 border-[#FAF7F2] shadow-md text-[#D4A574] font-bold text-xl" data-id="element-454">
                  {item.step}
                </div>
                <p className="text-[#2C2416] font-medium leading-relaxed px-4" data-id="element-455">
                  {item.text}
                </p>
              </motion.div>)}
          </div>

          {/* Benefits */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6" data-id="element-456">
            {[{
            icon: <Bell className="w-6 h-6 text-[#D4A574]" data-id="element-457" />,
            text: 'Push notifications that bypass email'
          }, {
            icon: <Clock className="w-6 h-6 text-[#2C2416]" data-id="element-458" />,
            text: 'Lock screen reminders before offers expire'
          }, {
            icon: <MapPin className="w-6 h-6 text-[#D4A574]" data-id="element-459" />,
            text: 'Geo-triggered alerts near your store'
          }, {
            icon: <Zap className="w-6 h-6 text-[#2C2416]" data-id="element-460" />,
            text: 'Real-time points balance updates'
          }].map((item, index) => <div key={index} className="bg-white p-6 rounded-xl border border-[#D4A57420] flex flex-col items-center text-center shadow-sm" data-id="element-461">
                <div className="mb-4" data-id="element-462">{item.icon}</div>
                <p className="text-[#8B7355] text-sm font-medium" data-id="element-463">
                  {item.text}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 4. INTEGRATION SCENARIOS CAROUSEL */}
      <section className="py-24 px-6 md:px-10 bg-white overflow-hidden" data-id="element-464">
        <div className="max-w-[1400px] mx-auto" data-id="element-465">
          <div className="text-center mb-16" data-id="element-466">
            <h2 className="text-[#2C2416] text-[clamp(36px,5vw,48px)] font-light tracking-[-2px] mb-4" data-id="element-467">
              Works With Your Existing Stack
            </h2>
          </div>

          <div className="flex overflow-x-auto pb-8 gap-6 md:grid md:grid-cols-2 lg:grid-cols-3 snap-x snap-mandatory scrollbar-hide" data-id="element-468">
            {[{
            title: 'Yotpo + Shopify POS',
            desc: "Customer's wallet pass contains their member ID. Staff scans at checkout, Yotpo awards points automatically."
          }, {
            title: 'Klaviyo Delivery Flows',
            desc: 'Wallet passes distributed through your existing Klaviyo welcome series or post-purchase flows.'
          }, {
            title: 'Okendo Reviews + Loyalty',
            desc: 'Combine review collection with scannable membership. One pass does both.'
          }, {
            title: 'Smile.io + Multiple Stores',
            desc: 'Single wallet pass works across all your locations. Customer never needs to remember their email.'
          }, {
            title: 'Subscription Programs',
            desc: 'Subscribers get a pass showing their plan, next shipment date, and member benefits. Push notifications for shipping updates.'
          }, {
            title: 'Rivo VIP Tiers',
            desc: 'Wallet pass displays current tier status. Auto-updates when customer levels up. Push notification celebrates the upgrade.'
          }].map((card, index) => <motion.div key={index} whileHover={{
            y: -5
          }} className="min-w-[300px] snap-center bg-[#FAF7F2] p-8 rounded-xl border border-[#D4A57420] shadow-md flex flex-col" data-id="element-469">
                <h3 className="text-xl font-medium text-[#2C2416] mb-4" data-id="element-470">
                  {card.title}
                </h3>
                <p className="text-[#8B7355] leading-relaxed" data-id="element-471">{card.desc}</p>
              </motion.div>)}
          </div>

          {/* WinePik Callout Banner */}
          <div className="mt-12 bg-[#FAF7F2] border-2 border-[#D4A574] rounded-xl p-8 md:p-10" data-id="element-472">
            <h3 className="text-2xl font-medium text-[#2C2416] mb-4" data-id="element-473">
              Wine Club Memberships
            </h3>
            <p className="text-[#8B7355] leading-relaxed mb-6" data-id="element-474">
              Digital wallet passes for wine clubs are coming soon through
              WinePik, a subscription wine club app launching on Shopify. We're
              the partner agency for WinePik. Members will get passes showing
              their tier, next allocation date, and cellar door benefits with
              scan-for-VIP recognition at the tasting room.
            </p>
            <a href="#form" className="inline-flex items-center text-[#D4A574] font-medium hover:text-[#2C2416] transition-colors" data-id="element-475">
              Interested in WinePik? Let us know
              <ChevronRight className="w-4 h-4 ml-1" data-id="element-476" />
            </a>
          </div>
        </div>
      </section>

      {/* 5. FEATURES GRID */}
      <section className="py-24 px-6 md:px-10 bg-[#FAF7F2]" data-id="element-477">
        <div className="max-w-[1400px] mx-auto" data-id="element-478">
          <div className="text-center mb-16" data-id="element-479">
            <h2 className="text-[#2C2416] text-[clamp(36px,5vw,48px)] font-light tracking-[-2px] mb-4" data-id="element-480">
              What's Included
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-481">
            {[{
            title: 'Branded Pass Design',
            desc: 'Custom passes matching your brand colours logo and style.',
            icon: <CreditCard className="w-6 h-6 text-[#D4A574]" data-id="element-482" />
          }, {
            title: 'Klaviyo Integration',
            desc: 'Passes distributed through your existing email flows.',
            icon: <Mail className="w-6 h-6 text-[#2C2416]" data-id="element-483" />
          }, {
            title: 'Shopify POS Scanning',
            desc: 'Works with Scan2Add app or any barcode scanner.',
            icon: <SmartphoneNfc className="w-6 h-6 text-[#D4A574]" data-id="element-484" />
          }, {
            title: 'Push Notifications',
            desc: "Send updates directly to customer's lock screen.",
            icon: <Bell className="w-6 h-6 text-[#2C2416]" data-id="element-485" />
          }, {
            title: 'Real-time Updates',
            desc: 'Points balance and tier status update automatically on the pass.',
            icon: <RefreshCw className="w-6 h-6 text-[#D4A574]" data-id="element-486" />
          }, {
            title: 'Analytics Dashboard',
            desc: 'Track downloads scans and engagement.',
            icon: <BarChart className="w-6 h-6 text-[#2C2416]" data-id="element-487" />
          }].map((feature, index) => <motion.div key={index} {...fadeIn} transition={{
            delay: index * 0.1
          }} className="bg-white p-8 rounded-xl border border-[#D4A57420] shadow-sm hover:shadow-md transition-shadow" data-id="element-488">
                <div className="w-12 h-12 bg-[#FAF7F2] rounded-full flex items-center justify-center mb-6" data-id="element-489">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-medium text-[#2C2416] mb-3" data-id="element-490">
                  {feature.title}
                </h3>
                <p className="text-[#8B7355] text-sm leading-relaxed" data-id="element-491">
                  {feature.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* 6. COMPATIBLE PLATFORMS */}
      <section className="py-24 px-6 md:px-10 bg-white" data-id="element-492">
        <div className="max-w-[1400px] mx-auto" data-id="element-493">
          <div className="text-center mb-16" data-id="element-494">
            <h2 className="text-[#2C2416] text-[clamp(36px,5vw,48px)] font-light tracking-[-2px] mb-4" data-id="element-495">
              Integrations
            </h2>
          </div>

          <div className="flex flex-wrap justify-center gap-4 md:gap-6 max-w-4xl mx-auto mb-12" data-id="element-496">
            {['Yotpo', 'Smile.io', 'Okendo', 'Rivo', 'Stamped.io', 'Klaviyo', 'Mailchimp', 'Shopify POS', 'Square'].map((platform, index) => <div key={index} className="px-6 py-3 bg-[#FAF7F2] rounded-lg text-[#2C2416] font-medium border border-[#D4A57420]" data-id="element-497">
                {platform}
              </div>)}
          </div>

          <div className="text-center" data-id="element-498">
            <p className="text-[#8B7355]" data-id="element-499">
              Don't see your platform?{' '}
              <a href="#form" className="text-[#D4A574] font-medium hover:underline" data-id="element-500">
                Ask us about custom integrations.
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* 7. WHO IT'S FOR */}
      <section className="py-24 px-6 md:px-10 bg-[#FAF7F2]" data-id="element-501">
        <div className="max-w-[1400px] mx-auto" data-id="element-502">
          <div className="text-center mb-16" data-id="element-503">
            <h2 className="text-[#2C2416] text-[clamp(36px,5vw,48px)] font-light tracking-[-2px] mb-4" data-id="element-504">
              Who This Is For
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-505">
            {[{
            title: 'Shopify Retail',
            desc: 'Merchants with physical stores and Shopify POS who want faster checkout identification.',
            icon: <ShoppingBag className="w-8 h-8 text-[#D4A574]" data-id="element-506" />
          }, {
            title: 'DTC Brands with VIP Programs',
            desc: 'Brands with tiers in Yotpo or Smile.io who need a new channel beyond email.',
            icon: <Users className="w-8 h-8 text-[#2C2416]" data-id="element-507" />
          }, {
            title: 'Wine and Hospitality',
            desc: 'Wine clubs with member tiers and cellar doors wanting to recognise members.',
            icon: <Layers className="w-8 h-8 text-[#D4A574]" data-id="element-508" />
          }].map((item, index) => <motion.div key={index} {...fadeIn} transition={{
            delay: index * 0.1
          }} className="bg-white p-10 rounded-xl border border-[#D4A57420] shadow-lg text-center hover:-translate-y-1 transition-all duration-300" data-id="element-509">
                <div className="w-16 h-16 bg-[#FAF7F2] rounded-2xl flex items-center justify-center mb-6 mx-auto" data-id="element-510">
                  {item.icon}
                </div>
                <h3 className="text-xl font-medium text-[#2C2416] mb-4" data-id="element-511">
                  {item.title}
                </h3>
                <p className="text-[#8B7355] leading-relaxed" data-id="element-512">{item.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* 8. PROCESS TIMELINE */}
      <section className="py-24 px-6 md:px-10 bg-white" data-id="element-513">
        <div className="max-w-[1400px] mx-auto" data-id="element-514">
          <div className="text-center mb-16" data-id="element-515">
            <h2 className="text-[#2C2416] text-[clamp(36px,5vw,48px)] font-light tracking-[-2px] mb-4" data-id="element-516">
              The Process
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-8" data-id="element-517">
            {[{
            step: '1. Discovery',
            desc: 'Audit your current loyalty stack.'
          }, {
            step: '2. Pass Design',
            desc: 'Custom branded passes.'
          }, {
            step: '3. Integration Setup',
            desc: 'Connect to Klaviyo, loyalty app, and Shopify POS.'
          }, {
            step: '4. Distribution Flows',
            desc: 'Build email and SMS flows for pass delivery.'
          }, {
            step: '5. Launch and Optimise',
            desc: 'Go live then iterate.'
          }].map((item, index) => <div key={index} className="relative" data-id="element-518">
                {/* Connector Line (Desktop) */}
                {index < 4 && <div className="hidden md:block absolute top-6 -right-4 w-8 h-0.5 bg-[#D4A57420]" data-id="element-519"></div>}

                <div className="bg-[#FAF7F2] p-6 rounded-xl border border-[#D4A57420] h-full" data-id="element-520">
                  <h3 className="text-lg font-medium text-[#2C2416] mb-2" data-id="element-521">
                    {item.step}
                  </h3>
                  <p className="text-[#8B7355] text-sm" data-id="element-522">{item.desc}</p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* 9. INTEREST FORM */}
      <section id="form" className="py-24 px-6 md:px-10 bg-[#FAF7F2]" data-id="element-523">
        <div className="max-w-[1400px] mx-auto" data-id="element-524">
          <div className="text-center mb-12" data-id="element-525">
            <h2 className="text-[#2C2416] text-[clamp(36px,5vw,48px)] font-light tracking-[-2px] mb-4" data-id="element-526">
              Interested in Digital Wallet Passes?
            </h2>
            <p className="text-[#8B7355] text-xl max-w-2xl mx-auto" data-id="element-527">
              Tell us about your setup and we'll show you what's possible.
            </p>
          </div>

          <div className="bg-white p-8 md:p-12 rounded-xl shadow-md max-w-3xl mx-auto" data-id="element-528">
            <form onSubmit={handleSubmit} className="space-y-6" data-id="element-529">
              {/* Business Name */}
              <div data-id="element-530">
                <label htmlFor="businessName" className="block text-[#2C2416] font-medium mb-2" data-id="element-531">
                  Business Name
                </label>
                <input type="text" id="businessName" name="businessName" value={formData.businessName} onChange={handleChange} required disabled={isSubmitting} className="w-full px-4 py-3 border border-[#D4A57440] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4A574] focus:border-transparent transition-all disabled:opacity-50" data-id="element-532" />
              </div>

              {/* Website URL */}
              <div data-id="element-533">
                <label htmlFor="website" className="block text-[#2C2416] font-medium mb-2" data-id="element-534">
                  Website URL
                </label>
                <input type="text" id="website" name="website" value={formData.website} onChange={handleChange} required disabled={isSubmitting} className="w-full px-4 py-3 border border-[#D4A57440] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4A574] focus:border-transparent transition-all disabled:opacity-50" data-id="element-535" />
              </div>

              {/* Current Loyalty App */}
              <div data-id="element-536">
                <label htmlFor="loyaltyApp" className="block text-[#2C2416] font-medium mb-2" data-id="element-537">
                  Current Loyalty App
                </label>
                <select id="loyaltyApp" name="loyaltyApp" value={formData.loyaltyApp} onChange={handleChange} required disabled={isSubmitting} className="w-full pl-4 pr-12 py-3 border border-[#D4A57440] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4A574] focus:border-transparent transition-all bg-white appearance-none disabled:opacity-50" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%232C2416' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 1rem center',
                backgroundSize: '12px 8px'
              }} data-id="element-538">
                  <option value="" data-id="element-539">Select an app</option>
                  <option value="Yotpo" data-id="element-540">Yotpo</option>
                  <option value="Smile.io" data-id="element-541">Smile.io</option>
                  <option value="Okendo" data-id="element-542">Okendo</option>
                  <option value="Rivo" data-id="element-543">Rivo</option>
                  <option value="Stamped.io" data-id="element-544">Stamped.io</option>
                  <option value="Other" data-id="element-545">Other</option>
                  <option value="None" data-id="element-546">None</option>
                </select>
              </div>

              {/* Has Retail */}
              <div data-id="element-547">
                <label htmlFor="hasRetail" className="block text-[#2C2416] font-medium mb-2" data-id="element-548">
                  Do you have physical retail?
                </label>
                <select id="hasRetail" name="hasRetail" value={formData.hasRetail} onChange={handleChange} required disabled={isSubmitting} className="w-full pl-4 pr-12 py-3 border border-[#D4A57440] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4A574] focus:border-transparent transition-all bg-white appearance-none disabled:opacity-50" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%232C2416' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 1rem center',
                backgroundSize: '12px 8px'
              }} data-id="element-549">
                  <option value="" data-id="element-550">Select an option</option>
                  <option value="Yes" data-id="element-551">Yes</option>
                  <option value="No" data-id="element-552">No</option>
                </select>
              </div>

              {/* Email */}
              <div data-id="element-553">
                <label htmlFor="email" className="block text-[#2C2416] font-medium mb-2" data-id="element-554">
                  Email
                </label>
                <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required disabled={isSubmitting} className="w-full px-4 py-3 border border-[#D4A57440] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4A574] focus:border-transparent transition-all disabled:opacity-50" data-id="element-555" />
              </div>

              {/* Message */}
              <div data-id="element-556">
                <label htmlFor="message" className="block text-[#2C2416] font-medium mb-2" data-id="element-557">
                  Message (Optional)
                </label>
                <textarea id="message" name="message" value={formData.message} onChange={handleChange} rows={4} disabled={isSubmitting} className="w-full px-4 py-3 border border-[#D4A57440] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4A574] focus:border-transparent transition-all resize-vertical disabled:opacity-50" data-id="element-558" />
              </div>

              {/* Success/Error Messages */}
              {submitStatus === 'success' && <div className="bg-[#D4A574] bg-opacity-10 border border-[#D4A574] text-[#2C2416] px-6 py-4 rounded-lg" data-id="element-559">
                  Thanks for reaching out. We will be in touch soon.
                </div>}

              {/* Submit Button */}
              <button type="submit" disabled={isSubmitting} className="w-full bg-[#2C2416] text-[#FAF7F2] px-9 py-[18px] rounded-full font-medium tracking-[0.5px] hover:scale-105 transition-all duration-300 hover:bg-[#D4A574] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100" data-id="element-560">
                {isSubmitting ? 'Sending...' : 'Request Information'}
              </button>

              <p className="text-center text-sm text-[#8B7355] mt-4" data-id="element-561">
                We'll get back to you within 48 hours.
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* 10. FOOTER CTA BANNER */}
      <section className="py-20 px-6 md:px-10" data-id="element-562">
        <div className="max-w-[1400px] mx-auto" data-id="element-563">
          <div className="bg-[#2C2416] text-center p-16 rounded-3xl" data-id="element-564">
            <h2 className="text-[#FAF7F2] text-[clamp(32px,5vw,48px)] font-light tracking-[-2px] mb-8" data-id="element-565">
              Ready to put your loyalty program in their wallet?
            </h2>
            <a href="#form" className="inline-block bg-[#D4A574] text-[#2C2416] px-9 py-[18px] rounded-full font-medium tracking-[0.5px] hover:scale-105 transition-all duration-300 hover:bg-[#FAF7F2]" data-id="element-566">
              Get Started
            </a>
          </div>
        </div>
      </section>

      <Footer data-id="element-567" />
    </div>;
}
// Helper component for icons
function RefreshCw(props: any) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-568">
      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" data-id="element-569" />
      <path d="M21 3v5h-5" data-id="element-570" />
      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" data-id="element-571" />
      <path d="M8 16H3v5" data-id="element-572" />
    </svg>;
}